import React from 'react';
import {
  Box,
  Typography,
  makeStyles,
  useTheme,
  Grid,
  useMediaQuery,
} from '@material-ui/core';
import Step from './step';

const generateSrcSet = (url: string) => {
  if (url.includes('.png')) {
    const fileName = url.replace('.png', '');
    return `${fileName}.png 1x, ${fileName}@2x.png 2x, ${fileName}@3x.png 3x`;
  }
};

const CONTENT = {
  step1: {
    title: 'Choose your personal coach',
    subTitle:
      'Find the perfect certified trainer according to your preferences',
    imageSrcMobile: 'how-it-works/find-trainer.png',
    imageSrcDesktop: 'how-it-works/find-trainer.png',
    mobileImageWidth: 338.86,
    mobileImageHeight: 461,
    desktopImageWidth: 381.14,
    desktopImageHeight: 517,
    alt: 'Personal trainers list and profile',
  },
  step2: {
    title: 'Connect with your personal trainer via messenger',
    subTitle:
      'Align on goals and preferences with the coach so he can create the best workouts for you',
    imageSrcMobile: 'how-it-works/connect-via-messenger-mobile.png',
    imageSrcDesktop: 'how-it-works/connect-via-messenger-desktop.png',
    mobileImageWidth: 307.33,
    mobileImageHeight: 514.43,
    desktopImageWidth: 369.5,
    desktopImageHeight: 618,
    alt: 'Connect with fitness coach via messenger',
  },
  step3: {
    title: 'Train and receive guidance from your trainer',
    subTitle:
      'By submitting your workout results and videos, you will receive detailed feedback on technique and progress.',
    imageSrcMobile: 'how-it-works/train-and-feedback-mobile.png',
    imageSrcDesktop: 'how-it-works/train-and-feedback-desktop.png',
    mobileImageWidth: 343,
    mobileImageHeight: 487.19,
    desktopImageWidth: 552,
    desktopImageHeight: 649.58,
    alt:
      'Messenger chat with personal trainer. Getting feedback. Submitting workout result.',
  },
};

const useStyles = makeStyles(({ breakpoints, spacing }) => ({
  heading: {
    margin: spacing(0, 2),
    [breakpoints.up('md')]: {
      margin: spacing(0, 8),
    },
  },
  contentContainer: {
    background: 'url("./how-it-works/arrow-mobile.svg")',
    backgroundRepeat: 'no-repeat',
    backgroundPositionX: '90%',
    backgroundSize: 'unset',
    marginTop: spacing(4),
    paddingBottom: spacing(18),
    [breakpoints.down('sm')]: {
      backgroundPositionY: '100%',
      backgroundSize: '294px',
    },
    [breakpoints.down('xs')]: {
      backgroundPositionY: '100%',
      backgroundSize: '80%',
    },
    [breakpoints.up(400)]: {
      backgroundSize: '294px',
    },
    [breakpoints.up('md')]: {
      background: 'url("./how-it-works/arrow-desktop.svg")',
      backgroundRepeat: 'no-repeat',
      backgroundPositionX: 'center',
      backgroundPositionY: 'bottom',
      // backgroundSize: '643px',
      marginLeft: spacing(6),
      marginRight: spacing(6),
    },
  },
  resultContainer: {
    margin: spacing(0, 2),
    [breakpoints.up('md')]: {
      margin: spacing(0, 8),
      maxWidth: '345px',
    },
  },
  imageContainer: {
    [breakpoints.up('md')]: {
      marginTop: spacing(-8),
      marginLeft: spacing(-8),
    },
  },
  image: {
    maxWidth: '100%',
    height: 'auto',
    width: 'auto',
  },
}));

export const HowItWorksSection = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  return (
    <>
      <Box className={classes.heading}>
        <Typography
          color="primary"
          variant={isDesktop ? 'h4' : 'h5'}
          component="h3"
        >
          How does it work?
        </Typography>
      </Box>
      <Box className={classes.contentContainer}>
        <Box mx={2}>
          {Object.entries(CONTENT).map((entries, index) => {
            const [key, value] = entries;
            const imageSrc = isDesktop
              ? value.imageSrcDesktop
              : value.imageSrcMobile;
            const imageSrcSet = generateSrcSet(imageSrc);
            const width = isDesktop
              ? value.desktopImageWidth
              : value.mobileImageWidth;
            const height = isDesktop
              ? value.desktopImageHeight
              : value.mobileImageHeight;
            return (
              <Grid container key={key}>
                <Grid item xs={12} md={6}>
                  <Step
                    stepNumber={index + 1}
                    title={value.title}
                    subTitle={value.subTitle}
                  />
                </Grid>
                <Grid className={classes.imageContainer} item xs={12} md={6}>
                  <img
                    width={`${width}px`}
                    height={`${height}px`}
                    src={imageSrc}
                    srcSet={imageSrcSet}
                    alt={value.alt}
                    className={classes.image}
                  />
                </Grid>
              </Grid>
            );
          })}
        </Box>
      </Box>
      <Box className={classes.resultContainer}>
        <Typography variant="subtitle1" component="h5">
          You can workout anytime and anywhere you want and still get full
          support on your training!
        </Typography>
      </Box>
    </>
  );
};
